import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, X } from "lucide-react"
import { useRef, useState } from "react"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha, hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type Dotacion = {
  id: number
  persona_id: number
  persona: string
  tipo: string
  descripcion: string
  cantidad: string
  detalle: string | null
  fecha: string
  del_deposito: boolean
  estado: "ENTREGADA" | "DEVUELTA" | "ANULADA"
  devuelta_en: string | null
  observacion: string | null
}

type Articulo = { id: number; nombre: string; existencia: string }

const TIPOS: Record<string, string> = {
  UNIFORME: "Uniforme",
  EQUIPO: "Equipo",
  HERRAMIENTA: "Herramienta",
  PROTECCION: "Protección personal",
  INSUMO: "Insumo",
}

type Renglon = {
  /** Sólo para la clave de React: los renglones se añaden y se quitan. */
  id: number
  tipo: string
  articulo_id: string
  descripcion: string
  detalle: string
  cantidad: string
}

let siguienteId = 0
const vacio = (): Renglon => ({
  id: ++siguienteId,
  tipo: "UNIFORME",
  articulo_id: "",
  descripcion: "",
  detalle: "",
  cantidad: "1",
})

/**
 * Talento Humano → Dotación (28/10, minuta del 16/10 §2.2 y pedido del usuario): lo que se
 * le entregó a cada trabajador —uniformes, equipos, herramientas, protección e insumos— y
 * lo que devolvió.
 *
 * ⚠ **Si sale del depósito, el depósito lo descuenta**: se elige el artículo y la entrega
 * es una salida de inventario a su nombre. Lo comprado directo para la persona se escribe
 * a mano, sin artículo.
 */
export function DotacionTH() {
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "dotacion:entregar")
  const clienteQuery = useQueryClient()
  const [nueva, setNueva] = useState(false)
  const [persona, setPersona] = useState("")
  const [fecha, setFecha] = useState(hoyLocal())
  const [renglones, setRenglones] = useState<Renglon[]>(() => [vacio()])
  const [motivo, setMotivo] = useState<Record<number, string>>({})

  const lista = useQuery({
    queryKey: ["dotaciones"],
    queryFn: () => api.get<Dotacion[]>("/api/v1/dotaciones"),
  })
  const personas = useQuery({
    queryKey: ["personas", "empleados"],
    queryFn: () =>
      api.get<{ items: { id: number; nombres: string; apellidos: string; tipo: string }[] }>(
        "/api/v1/personas?estado=ACTIVO&limit=200",
      ),
    enabled: nueva,
  })
  const articulos = useQuery({
    queryKey: ["articulos", "dotacion"],
    queryFn: () => api.get<Articulo[]>("/api/v1/articulos"),
    enabled: nueva,
  })
  const refrescar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["dotaciones"] })
    clienteQuery.invalidateQueries({ queryKey: ["articulos"] })
  }
  const entregar = useMutation({
    mutationFn: () =>
      api.post("/api/v1/dotaciones", {
        persona_id: Number(persona),
        fecha,
        items: renglones.map((r) => ({
          tipo: r.tipo,
          descripcion: r.descripcion,
          detalle: r.detalle || null,
          cantidad: r.cantidad,
          articulo_id: r.articulo_id === "" ? null : Number(r.articulo_id),
        })),
      }),
    onSuccess: () => {
      refrescar()
      setNueva(false)
      setRenglones([vacio()])
    },
  })
  const devolver = useMutation({
    mutationFn: (id: number) => api.post(`/api/v1/dotaciones/${id}/devolver`, {}),
    onSuccess: refrescar,
  })
  const anular = useMutation({
    mutationFn: (id: number) =>
      api.post(`/api/v1/dotaciones/${id}/anular`, { motivo: motivo[id] ?? "" }),
    onSuccess: refrescar,
  })
  const poner = (i: number, cambio: Partial<Renglon>) =>
    setRenglones((rs) => rs.map((r, j) => (j === i ? { ...r, ...cambio } : r)))
  const error = entregar.error ?? devolver.error ?? anular.error
  const vista = useRef<HTMLDivElement | null>(null)

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Lo que tiene cada trabajador. Lo que sale del depósito se descuenta del inventario.
        </p>
        <div className="flex flex-wrap items-start gap-2">
          {!!lista.data?.length && (
            <Exportar
              titulo="Dotación del personal"
              subtitulo="Lo entregado a cada trabajador y lo devuelto"
              contenido={vista}
            />
          )}
          {puede && !nueva && (
            <Boton variante="primario" onClick={() => setNueva(true)}>
              <Plus size={16} /> Nueva entrega
            </Boton>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="tarjeta p-3 text-sm" style={{ color: "var(--destructive)" }}>
          {(error as Error).message}
        </p>
      )}

      {nueva && (
        <form
          className="tarjeta space-y-3 p-4"
          onSubmit={(e) => {
            e.preventDefault()
            entregar.mutate()
          }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="text-sm sm:col-span-2">
              A quién
              <select
                className="campo mt-1"
                required
                value={persona}
                onChange={(e) => setPersona(e.target.value)}
              >
                <option value="">Elige…</option>
                {(personas.data?.items ?? [])
                  .filter((p) => p.tipo !== "CLIENTE")
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombres} {p.apellidos}
                    </option>
                  ))}
              </select>
            </label>
            <label className="text-sm">
              Fecha
              <input
                type="date"
                className="campo mt-1"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </label>
          </div>
          {renglones.map((r, i) => (
            <div
              key={r.id}
              className="grid gap-2 sm:grid-cols-[9rem_1fr_1fr_8rem_5rem_auto] sm:items-end"
            >
              <label className="text-xs">
                Tipo
                <select
                  className="campo mt-1"
                  value={r.tipo}
                  onChange={(e) => poner(i, { tipo: e.target.value })}
                >
                  {Object.entries(TIPOS).map(([v, t]) => (
                    <option key={v} value={v}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs">
                Del depósito (opcional)
                <select
                  className="campo mt-1"
                  value={r.articulo_id}
                  onChange={(e) => poner(i, { articulo_id: e.target.value })}
                >
                  <option value="">No: comprado directo</option>
                  {(articulos.data ?? []).map((a) => (
                    <option key={a.id} value={a.id} disabled={Number(a.existencia) <= 0}>
                      {a.nombre} · quedan {Number(a.existencia)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs">
                Qué es
                <input
                  className="campo mt-1"
                  placeholder={r.articulo_id ? "El nombre del artículo" : "Camisa con logo"}
                  required={r.articulo_id === ""}
                  value={r.descripcion}
                  onChange={(e) => poner(i, { descripcion: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Talla o serial
                <input
                  className="campo mt-1"
                  value={r.detalle}
                  onChange={(e) => poner(i, { detalle: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Cantidad
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  className="campo mt-1"
                  value={r.cantidad}
                  onChange={(e) => poner(i, { cantidad: e.target.value })}
                />
              </label>
              {renglones.length > 1 ? (
                <Boton
                  tamano="sm"
                  aria-label="Quitar renglón"
                  onClick={() => setRenglones((rs) => rs.filter((_, j) => j !== i))}
                >
                  <X size={14} />
                </Boton>
              ) : (
                <span />
              )}
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Boton tamano="sm" onClick={() => setRenglones((rs) => [...rs, vacio()])}>
              <Plus size={14} /> Otra cosa
            </Boton>
            <span className="flex-1" />
            <Boton onClick={() => setNueva(false)}>Cancelar</Boton>
            <Boton type="submit" variante="primario" disabled={entregar.isPending}>
              {entregar.isPending ? "Entregando…" : "Registrar la entrega"}
            </Boton>
          </div>
        </form>
      )}

      {lista.isPending && <EstadoCarga etiqueta="Cargando la dotación" />}
      {lista.isError && (
        <EstadoError
          titulo="No se pudo cargar"
          error={lista.error}
          onReintentar={() => lista.refetch()}
        />
      )}
      {lista.data && (
        <div ref={vista}>
          <Tabla etiqueta="Dotación entregada">
            <Cabecera>
              <Th>Quién y qué</Th>
              <Th>Entregada</Th>
              <Th className="text-right">Cantidad</Th>
              <Th>Estado</Th>
              <Th>
                <span className="sr-only">Acciones</span>
              </Th>
            </Cabecera>
            <Cuerpo>
              {lista.data.length === 0 && (
                <TablaVacia colSpan={5}>Todavía no se ha entregado dotación.</TablaVacia>
              )}
              {lista.data.map((d) => (
                <Fila key={d.id}>
                  <Td>
                    <div className="font-medium">{d.persona}</div>
                    <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                      {TIPOS[d.tipo] ?? d.tipo} · {d.descripcion}
                      {d.detalle && ` · ${d.detalle}`}
                      {d.del_deposito && " · del depósito"}
                    </div>
                  </Td>
                  <Td className="tabular-nums">{comoFecha(d.fecha)}</Td>
                  <Td
                    className="text-right tabular-nums"
                    data-exportar-valor={String(Number(d.cantidad)).replace(".", ",")}
                  >
                    {Number(d.cantidad)}
                  </Td>
                  <Td>
                    <Insignia
                      tono={
                        d.estado === "ENTREGADA"
                          ? "info"
                          : d.estado === "DEVUELTA"
                            ? "exito"
                            : "neutro"
                      }
                    >
                      {d.estado === "ENTREGADA"
                        ? "La tiene"
                        : d.estado === "DEVUELTA"
                          ? `Devuelta ${comoFecha(d.devuelta_en)}`
                          : "Anulada"}
                    </Insignia>
                  </Td>
                  <Td>
                    {puede && d.estado === "ENTREGADA" && (
                      <div className="flex flex-wrap items-center gap-2">
                        <Boton tamano="sm" onClick={() => devolver.mutate(d.id)}>
                          Devolvió
                        </Boton>
                        <input
                          aria-label="Motivo para anular"
                          placeholder="Motivo para anular"
                          className="campo h-8 w-40 text-xs"
                          value={motivo[d.id] ?? ""}
                          onChange={(e) => setMotivo({ ...motivo, [d.id]: e.target.value })}
                        />
                        <Boton
                          tamano="sm"
                          variante="peligro"
                          disabled={(motivo[d.id] ?? "").trim().length < 10}
                          onClick={() => anular.mutate(d.id)}
                        >
                          Anular
                        </Boton>
                      </div>
                    )}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        </div>
      )}
    </section>
  )
}
